import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const QuestionReview = ({ questions }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [showExplanation, setShowExplanation] = useState(false);
  const [filterType, setFilterType] = useState('all');

  const filteredQuestions = questions?.filter(q => {
    if (filterType === 'correct') return q?.isCorrect;
    if (filterType === 'incorrect') return !q?.isCorrect;
    return true;
  });

  const currentQuestion = filteredQuestions?.[currentQuestionIndex];

  const handleNext = () => {
    if (currentQuestionIndex < filteredQuestions?.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setShowExplanation(false);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      setShowExplanation(false);
    }
  };

  const handleJumpToQuestion = (index) => {
    setCurrentQuestionIndex(index);
    setShowExplanation(false);
  };

  return (
    <div className="bg-card rounded-xl p-6 md:p-8 shadow-elevation-2 animate-fade-in">
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-6">
        <div>
          <h3 className="text-xl md:text-2xl font-semibold text-foreground mb-2">
            Question-by-Question Review
          </h3>
          <p className="text-sm md:text-base text-muted-foreground">
            Detailed analysis with explanations
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant={filterType === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => {
              setFilterType('all');
              setCurrentQuestionIndex(0);
            }}
          >
            All ({questions?.length})
          </Button>
          <Button
            variant={filterType === 'correct' ? 'success' : 'outline'}
            size="sm"
            onClick={() => {
              setFilterType('correct');
              setCurrentQuestionIndex(0);
            }}
          >
            Correct ({questions?.filter(q => q?.isCorrect)?.length})
          </Button>
          <Button
            variant={filterType === 'incorrect' ? 'destructive' : 'outline'}
            size="sm"
            onClick={() => {
              setFilterType('incorrect');
              setCurrentQuestionIndex(0);
            }}
          >
            Incorrect ({questions?.filter(q => !q?.isCorrect)?.length})
          </Button>
        </div>
      </div>
      {filteredQuestions?.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="FileQuestion" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-base text-muted-foreground">
            No questions match the selected filter
          </p>
        </div>
      ) : (
        <>
          <div className="bg-muted/30 rounded-lg p-4 md:p-6 border border-border mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <span className="px-3 py-1 bg-primary/20 text-primary text-sm font-semibold rounded-full data-text">
                  Question {currentQuestionIndex + 1} of {filteredQuestions?.length}
                </span>
                <span className="px-3 py-1 bg-muted text-muted-foreground text-xs rounded-full">
                  {currentQuestion?.knowledgeArea}
                </span>
              </div>
              <div className={`w-8 h-8 rounded-full ${currentQuestion?.isCorrect ? 'bg-success' : 'bg-error'} flex items-center justify-center`}>
                <Icon 
                  name={currentQuestion?.isCorrect ? 'Check' : 'X'} 
                  size={18} 
                  color="white" 
                />
              </div>
            </div>

            <p className="text-base md:text-lg text-foreground mb-6 leading-relaxed">
              {currentQuestion?.question}
            </p>

            <div className="space-y-3">
              {currentQuestion?.options?.map((option, index) => {
                const isUserAnswer = option === currentQuestion?.userAnswer;
                const isCorrectAnswer = option === currentQuestion?.correctAnswer;
                
                let optionClass = 'bg-muted/50 border-border';
                if (isCorrectAnswer) {
                  optionClass = 'bg-success/10 border-success';
                } else if (isUserAnswer && !isCorrectAnswer) {
                  optionClass = 'bg-error/10 border-error';
                }

                return (
                  <div
                    key={index}
                    className={`p-4 rounded-lg border-2 ${optionClass} transition-smooth`}
                  >
                    <div className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-background flex items-center justify-center text-sm font-semibold flex-shrink-0 mt-0.5">
                        {String.fromCharCode(65 + index)}
                      </span>
                      <p className="text-sm md:text-base text-foreground flex-1">
                        {option}
                      </p>
                      {isCorrectAnswer && (
                        <Icon name="CheckCircle2" size={20} className="text-success flex-shrink-0" />
                      )}
                      {isUserAnswer && !isCorrectAnswer && (
                        <Icon name="XCircle" size={20} className="text-error flex-shrink-0" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 pt-6 border-t border-border">
              <Button
                variant="outline"
                size="sm"
                iconName={showExplanation ? 'ChevronUp' : 'ChevronDown'}
                iconPosition="right"
                onClick={() => setShowExplanation(!showExplanation)}
                fullWidth
              >
                {showExplanation ? 'Hide' : 'Show'} Detailed Explanation
              </Button>

              {showExplanation && (
                <div className="mt-4 p-4 bg-primary/5 rounded-lg border border-primary/20 animate-slide-down">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <Icon name="Lightbulb" size={18} className="text-primary" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-foreground mb-2">
                        Explanation
                      </h4>
                      <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                        {currentQuestion?.explanation}
                      </p>
                      
                      <div className="bg-card rounded-lg p-3 border border-border">
                        <p className="text-xs font-medium text-muted-foreground mb-2">
                          BABOK Reference:
                        </p>
                        <p className="text-sm text-foreground">
                          {currentQuestion?.babokReference}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <Button
              variant="outline"
              iconName="ChevronLeft"
              iconPosition="left"
              onClick={handlePrevious}
              disabled={currentQuestionIndex === 0}
            >
              Previous
            </Button>

            <div className="flex items-center gap-2 overflow-x-auto max-w-full px-2">
              {filteredQuestions?.slice(
                Math.max(0, currentQuestionIndex - 2),
                Math.min(filteredQuestions?.length, currentQuestionIndex + 3)
              )?.map((_, idx) => {
                const actualIndex = Math.max(0, currentQuestionIndex - 2) + idx;
                const question = filteredQuestions?.[actualIndex];
                return (
                  <button
                    key={actualIndex}
                    onClick={() => handleJumpToQuestion(actualIndex)}
                    className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-semibold transition-smooth flex-shrink-0 ${
                      actualIndex === currentQuestionIndex
                        ? 'bg-primary text-primary-foreground'
                        : question?.isCorrect
                        ? 'bg-success/20 text-success hover:bg-success/30' :'bg-error/20 text-error hover:bg-error/30'
                    }`}
                  >
                    {actualIndex + 1}
                  </button>
                );
              })}
            </div>

            <Button
              variant="outline"
              iconName="ChevronRight"
              iconPosition="right"
              onClick={handleNext}
              disabled={currentQuestionIndex === filteredQuestions?.length - 1}
            >
              Next
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default QuestionReview;